// Native-app link plumbing, both directions:
//
// 1. registerDeepLinkHandler(): inside the Capacitor shell, a universal
//    link (e.g. an NFC tag at /t/<token>) opens the APP, route the
//    webview to that path instead of leaving it wherever it last was.
//    Handles both warm opens (appUrlOpen) and cold starts (getLaunchUrl).
//
// 2. installSmartAppBanner(): in Safari, advertise the venue's published
//    iOS app so a tap without the app installed offers "Download", the
//    App Store fallback for NFC tags. No-op unless the venue has an
//    appStoreId configured and we're NOT already inside the app.
import { Capacitor } from "@capacitor/core";
import { router } from "../router";

export function registerDeepLinkHandler(): void {
  if (!Capacitor.isNativePlatform()) return;
  import("@capacitor/app").then(({ App }) => {
    const navigate = (url: string) => {
      try {
        const u = new URL(url);
        router.history.push(u.pathname + u.search);
      } catch {
        // not a URL we can route, ignore
      }
    };
    App.addListener("appUrlOpen", ({ url }) => navigate(url));
    App.getLaunchUrl().then((launch) => {
      if (launch?.url) navigate(launch.url);
    });
  });
}

// Run a callback whenever the native app returns to the foreground. Used to
// re-validate the session on resume, webviews can sit backgrounded for days,
// and we want the auth state checked against the server the moment the user
// comes back rather than on their next protected action. No-op on web.
export function registerSessionRefresh(onForeground: () => void): void {
  if (!Capacitor.isNativePlatform()) return;
  import("@capacitor/app").then(({ App }) => {
    App.addListener("resume", () => onForeground());
  });
}

export function installSmartAppBanner(appStoreId: string | undefined): void {
  if (!appStoreId || Capacitor.isNativePlatform()) return;
  if (document.querySelector('meta[name="apple-itunes-app"]')) return;
  const meta = document.createElement("meta");
  meta.name = "apple-itunes-app";
  // app-argument carries the current URL into the app on "Open".
  meta.content = `app-id=${appStoreId}, app-argument=${window.location.href}`;
  document.head.appendChild(meta);
}
